import React, { useState } from "react";
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Form,
  Accordion,
  ListGroup,
  Ratio,
  Alert,
  Stack
} from "react-bootstrap";
import {
  CloudCheck,
  ShieldLock,
  Gear,
  Diagram3,
  Rocket,
  Phone,
  Envelope,
  ArrowRight,
  CheckCircle,
  Cpu,
  Building,
  PersonBadge,
  Trophy,
  StarFill,
  ClockHistory
} from "react-bootstrap-icons";

// NOTE: Ensure you have Bootstrap CSS imported once in your app entry (e.g., index.tsx):
// import 'bootstrap/dist/css/bootstrap.min.css';

// Quick install:
//   npm i react-bootstrap bootstrap react-bootstrap-icons

const brandColor = "#232F3E"; // AWS console navy
const accentColor = "#FF9900"; // AWS orange

const Pill: React.FC<{ label: string }> = ({ label }) => (
  <Badge bg="warning" text="dark" className="me-2 mb-2" style={{ backgroundColor: accentColor }}>
    {label}
  </Badge>
);

const FeatureItem: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body>
      <Stack direction="horizontal" gap={3} className="mb-2">
        <div className="d-inline-flex align-items-center justify-content-center rounded" style={{ width: 44, height: 44, background: "#F3F4F6" }}>
          {icon}
        </div>
        <Card.Title as="h5" className="mb-0">{title}</Card.Title>
      </Stack>
      <Card.Text className="text-secondary mb-0">{desc}</Card.Text>
    </Card.Body>
  </Card>
);

const Stat: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className="text-center p-3">
    <div className="fw-bold display-6" style={{ color: brandColor }}>{value}</div>
    <div className="text-secondary">{label}</div>
  </div>
);

const Testimonial: React.FC<{ quote: string; name: string; role: string }> = ({ quote, name, role }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body>
      <div className="mb-2" aria-hidden>
        {[...Array(5)].map((_, i) => (
          <StarFill key={i} className="me-1" />
        ))}
      </div>
      <blockquote className="blockquote mb-3">“{quote}”</blockquote>
      <figcaption className="blockquote-footer mb-0">
        <span className="fw-semibold">{name}</span> · {role}
      </figcaption>
    </Card.Body>
  </Card>
);

const ContactForm: React.FC = () => {
  const [validated, setValidated] = useState(false);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.preventDefault();
      e.stopPropagation();
    }
    setValidated(true);
  };

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit} className="p-4 p-md-5 shadow-sm bg-white rounded-3">
      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="name">
            <Form.Label>Full name</Form.Label>
            <Form.Control required type="text" placeholder="Jane Doe" />
            <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control required type="email" placeholder="you@company.com" />
            <Form.Control.Feedback type="invalid">A valid email is required.</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="company">
            <Form.Label>Company</Form.Label>
            <Form.Control type="text" placeholder="Acme, Inc." />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="role">
            <Form.Label>Your role</Form.Label>
            <Form.Select defaultValue="">
              <option value="" disabled>
                Select
              </option>
              <option>Founder / Owner</option>
              <option>Engineering</option>
              <option>Product</option>
              <option>IT / Ops / DevOps</option>
              <option>Finance</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group controlId="services">
            <Form.Label>Interested in</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {[
                "AWS Architecture Review",
                "Migration to AWS",
                "Cost Optimization",
                "Serverless Apps",
                "Data & Analytics",
                "Security & Compliance"
              ].map((s) => (
                <Form.Check key={s} type="checkbox" id={`svc-${s}`} label={s} />
              ))}
            </div>
          </Form.Group>
        </Col>
        <Col xs={12}>
          <Form.Group controlId="message">
            <Form.Label>Project summary</Form.Label>
            <Form.Control as="textarea" rows={4} placeholder="Briefly describe your goals, constraints, and timeline." />
          </Form.Group>
        </Col>
        <Col md={6} className="d-grid">
          <Button type="submit" size="lg" style={{ backgroundColor: brandColor }}>
            Request a consult <ArrowRight className="ms-1" />
          </Button>
        </Col>
        <Col md={6}>
          <Alert variant="light" className="h-100 mb-0 d-flex align-items-center">
            <CheckCircle className="me-2" /> 24–48h response · No obligation
          </Alert>
        </Col>
      </Row>
    </Form>
  );
};

const Hero: React.FC = () => (
  <section style={{
    background: `linear-gradient(120deg, ${brandColor} 0%, #0B253A 60%)`,
    color: "#fff"
  }}>
    <Container className="py-5 py-lg-6">
      <Row className="align-items-center g-4">
        <Col lg={7}>
          <div className="mb-3">
            <Pill label="AWS Consultant" />
            <Pill label="Engineering" />
            <Pill label="Architect" />
          </div>
          <h1 className="display-5 fw-bold mb-3">
            Build faster, safer, and cheaper on AWS
          </h1>
          <p className="lead mb-4 text-white-50">
            Hands‑on AWS Engineering and Architecture services: migrations, well‑architected designs, serverless apps, containers, data pipelines, cost control, and security by default.
          </p>
          <div className="d-flex flex-wrap gap-2">
            <Button as="a" href="#contact" size="lg" style={{ backgroundColor: accentColor, borderColor: accentColor }} className="text-dark fw-semibold">
              Book a free consult
            </Button>
            <Button as="a" href="#services" variant="outline-light" size="lg">
              Explore services
            </Button>
          </div>
          <div className="mt-4 d-flex flex-wrap align-items-center text-white-50">
            <ClockHistory className="me-2" /> Typical kickoff within 1–2 weeks
          </div>
        </Col>
        <Col lg={5}>
          <Card bg="dark" text="light" className="border-0 shadow-lg">
            <Card.Body>
              <Stack gap={3}>
                <div className="d-flex align-items-center">
                  <div className="me-3 d-inline-flex align-items-center justify-content-center rounded" style={{ width: 44, height: 44, background: "rgba(255,255,255,0.1)" }}>
                    <CloudCheck size={22} />
                  </div>
                  <div>
                    <div className="small text-white-50">Core focus</div>
                    <div className="fw-semibold">Well‑Architected, Serverless, Containers</div>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="me-3 d-inline-flex align-items-center justify-content-center rounded" style={{ width: 44, height: 44, background: "rgba(255,255,255,0.1)" }}>
                    <ShieldLock size={22} />
                  </div>
                  <div>
                    <div className="small text-white-50">Security first</div>
                    <div className="fw-semibold">IAM least privilege · Guardrails · Compliance</div>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="me-3 d-inline-flex align-items-center justify-content-center rounded" style={{ width: 44, height: 44, background: "rgba(255,255,255,0.1)" }}>
                    <Gear size={22} />
                  </div>
                  <div>
                    <div className="small text-white-50">DevEx</div>
                    <div className="fw-semibold">IaC, GitOps, CI/CD, Observability</div>
                  </div>
                </div>
              </Stack>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </section>
);

const Services: React.FC = () => (
  <section id="services" className="py-5">
    <Container>
      <Row className="mb-4">
        <Col lg={{ span: 8, offset: 2 }} className="text-center">
          <h2 className="fw-bold">AWS Services</h2>
          <p className="text-secondary mb-0">Architecture, hands‑on engineering, and embedded enablement to move faster with confidence.</p>
        </Col>
      </Row>
      <Row className="g-4">
        <Col md={6} lg={4}>
          <FeatureItem
            icon={<Diagram3 />}
            title="Well‑Architected Reviews"
            desc="Assess workloads across the 6 pillars. Prioritized roadmap with quick wins and strategic refactors."
          />
        </Col>
        <Col md={6} lg={4}>
          <FeatureItem
            icon={<Rocket />}
            title="Migrations & Greenfield"
            desc="Rehost to re‑platform to re‑factor. Landing zones, org guardrails, VPC design, and app cutovers."
          />
        </Col>
        <Col md={6} lg={4}>
          <FeatureItem
            icon={<Cpu />}
            title="Serverless & Containers"
            desc="Lambda, API Gateway, DynamoDB, Step Functions; or EKS/ECS with least‑privilege IAM & GitOps."
          />
        </Col>
        <Col md={6} lg={4}>
          <FeatureItem
            icon={<ShieldLock />}
            title="Security & Compliance"
            desc="CIS foundations, IAM boundaries, detective controls, encryption, audit readiness (SOC2/HIPAA)."
          />
        </Col>
        <Col md={6} lg={4}>
          <FeatureItem
            icon={<Gear />}
            title="Platform & DevOps"
            desc="IaC (CDK/Terraform), CI/CD, environments, test data, observability (CloudWatch, X‑Ray, OpenTelemetry)."
          />
        </Col>
        <Col md={6} lg={4}>
          <FeatureItem
            icon={<CloudCheck />}
            title="Cost Optimization"
            desc="Right‑size, autoscale, storage tiers, Savings Plans, and architecture tweaks that cut the bill."
          />
        </Col>
      </Row>
    </Container>
  </section>
);

const LogosBar: React.FC = () => (
  <section className="py-4 bg-light border-top border-bottom">
    <Container>
      <Row className="g-4 align-items-center justify-content-center text-center">
        {["Healthcare", "Fintech", "SaaS", "Public Sector", "E‑commerce"].map((s, i) => (
          <Col key={i} xs={6} md={2} className="text-secondary small">
            <span className="d-inline-block py-2 px-3 bg-white rounded border shadow-sm w-100">{s}</span>
          </Col>
        ))}
      </Row>
    </Container>
  </section>
);

const Process: React.FC = () => (
  <section className="py-5 bg-light">
    <Container>
      <Row className="mb-4">
        <Col lg={{ span: 8, offset: 2 }} className="text-center">
          <h2 className="fw-bold">How we work</h2>
          <p className="text-secondary mb-0">Lean, collaborative, and outcome‑driven—from discovery to delivery.</p>
        </Col>
      </Row>
      <Row className="g-4">
        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-2"><PersonBadge className="me-2"/> Discover</div>
              <Card.Text className="text-secondary mb-0">Stakeholder interviews, architecture review, success criteria, and a joint plan.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-2"><Building className="me-2"/> Design</div>
              <Card.Text className="text-secondary mb-0">Reference architectures, guardrails, IaC baselines, and security patterns.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-2"><Gear className="me-2"/> Build</div>
              <Card.Text className="text-secondary mb-0">Hands‑on engineering with demos, tests, and CI/CD to production.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-2"><Trophy className="me-2"/> Enable</div>
              <Card.Text className="text-secondary mb-0">Knowledge transfer, docs, and enablement so your team runs with it.</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </section>
);

const FAQ: React.FC = () => (
  <section className="py-5">
    <Container>
      <Row className="mb-4">
        <Col lg={{ span: 8, offset: 2 }} className="text-center">
          <h2 className="fw-bold">FAQ</h2>
          <p className="text-secondary mb-0">Straight answers to common questions.</p>
        </Col>
      </Row>
      <Row>
        <Col lg={{ span: 8, offset: 2 }}>
          <Accordion defaultActiveKey="0">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Do you offer fixed‑price packages?</Accordion.Header>
              <Accordion.Body>
                Yes—clear deliverables for reviews, landing zones, and migration planning. For builds, we can do time‑and‑materials or milestone pricing.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Can you work with our existing team?</Accordion.Header>
              <Accordion.Body>
                Absolutely. We embed with product, security, and platform teams, focusing on enablement and durable patterns—not black‑box deliverables.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>What about compliance and security?</Accordion.Header>
              <Accordion.Body>
                We apply security by default: least‑privilege IAM, network segmentation, encryption, and detective controls—aligned to frameworks like CIS, NIST, SOC 2, or HIPAA.
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>
      </Row>
    </Container>
  </section>
);

const CTA: React.FC = () => (
  <section className="py-5" style={{ background: brandColor, color: "#fff" }}>
    <Container>
      <Row className="align-items-center g-4">
        <Col lg={8}>
          <h2 className="fw-bold mb-2">Ready to ship your next AWS milestone?</h2>
          <p className="mb-0 text-white-50">Schedule a free 30‑minute consult. We'll map your fastest path to value.</p>
        </Col>
        <Col lg={4} className="d-grid gap-2">
          <Button as="a" href="#contact" size="lg" style={{ backgroundColor: accentColor, borderColor: accentColor }} className="text-dark fw-semibold">
            Get started <ArrowRight className="ms-1" />
          </Button>
          <div className="text-white-50 small">
            Or reach us directly: <Phone className="me-1"/> <a className="link-light" href="tel:+18005551234">(800) 555‑1234</a>
            <span className="mx-2">·</span>
            <Envelope className="me-1"/> <a className="link-light" href="mailto:hello@aws‑consultant.dev">hello@aws‑consultant.dev</a>
          </div>
        </Col>
      </Row>
    </Container>
  </section>
);

const Footer: React.FC = () => (
  <footer className="py-4 bg-light">
    <Container>
      <Row className="align-items-center">
        <Col md={6} className="small text-secondary">© {new Date().getFullYear()} AWS Consultant. Independent consultancy—built on AWS best practices.</Col>
        <Col md={6}>
          <div className="d-flex justify-content-md-end gap-3 small">
            <a className="link-secondary" href="#services">Services</a>
            <a className="link-secondary" href="#process">Process</a>
            <a className="link-secondary" href="#contact">Contact</a>
          </div>
        </Col>
      </Row>
    </Container>
  </footer>
);

const AWSConsultantLanding_2025_11_07: React.FC = () => {
  return (
    <div>
      {/* Nav */}
      <Navbar expand="lg" bg="white" className="border-bottom shadow-sm">
        <Container>
          <Navbar.Brand href="#" className="fw-bold" style={{ color: brandColor }}>
            <span className="me-2">☁️</span> AWS Consultant
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="nav" />
          <Navbar.Collapse id="nav">
            <Nav className="ms-auto align-items-lg-center">
              <Nav.Link href="#services">Services</Nav.Link>
              <Nav.Link href="#process">Process</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
              <Button as="a" href="#contact" className="ms-lg-2" style={{ backgroundColor: brandColor }}>
                Contact
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <Hero />

      {/* Stats */}
      <section className="py-4">
        <Container>
          <Row>
            <Col md={4}><Stat value="100+" label="AWS workloads shipped"/></Col>
            <Col md={4}><Stat value="40%" label="Avg. cost savings identified"/></Col>
            <Col md={4}><Stat value="< 90 days" label="Typical time to value"/></Col>
          </Row>
        </Container>
      </section>

      {/* Logos */}
      <LogosBar />

      {/* Services */}
      <Services />

      {/* Process */}
      <div id="process">
        <Process />
      </div>

      {/* Testimonials */}
      <section className="py-5 bg-white">
        <Container>
          <Row className="mb-4">
            <Col lg={{ span: 8, offset: 2 }} className="text-center">
              <h2 className="fw-bold">What clients say</h2>
              <p className="text-secondary mb-0">Real outcomes from real teams.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}><Testimonial quote="Our migration finished ahead of schedule and under budget—while improving reliability." name="Maya R." role="VP Engineering, Fintech"/></Col>
            <Col md={6} lg={4}><Testimonial quote="They built our serverless MVP with IaC and CI/CD—we shipped in weeks, not months." name="Tom S." role="CTO, SaaS"/></Col>
            <Col md={6} lg={4}><Testimonial quote="The Well‑Architected review paid for itself quickly in cost and stability gains." name="Priya K." role="Director of Platform"/></Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <div id="faq">
        <FAQ />
      </div>

      {/* Contact */}
      <section id="contact" className="py-5 bg-light">
        <Container>
          <Row className="mb-4">
            <Col lg={{ span: 8, offset: 2 }} className="text-center">
              <h2 className="fw-bold">Let’s talk about your AWS roadmap</h2>
              <p className="text-secondary mb-0">Tell us about your goals, and we’ll suggest a pragmatic path to get there.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col lg={7}>
              <ContactForm />
            </Col>
            <Col lg={5}>
              <Card className="border-0 shadow-sm h-100">
                <ListGroup variant="flush">
                  <ListGroup.Item className="py-3">
                    <div className="fw-semibold mb-1">Popular starting points</div>
                    <ul className="mb-0 text-secondary">
                      <li>Well‑Architected Review & roadmap</li>
                      <li>Foundational landing zone + guardrails</li>
                      <li>Cost optimization sprint</li>
                    </ul>
                  </ListGroup.Item>
                  <ListGroup.Item className="py-3">
                    <div className="fw-semibold mb-1">Tech stack</div>
                    <div className="text-secondary">AWS CDK / Terraform · Lambda · API Gateway · DynamoDB · S3 · Step Functions · EventBridge · ECS/EKS · RDS/Aurora · CloudFront · Cognito · OpenSearch</div>
                  </ListGroup.Item>
                  <ListGroup.Item className="py-3">
                    <div className="fw-semibold mb-1">Contact</div>
                    <div className="text-secondary">
                      <Phone className="me-2" /> <a href="tel:+18005551234">(800) 555‑1234</a><br />
                      <Envelope className="me-2" /> <a href="mailto:hello@aws‑consultant.dev">hello@aws‑consultant.dev</a>
                    </div>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <CTA />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AWSConsultantLanding_2025_11_07;
